import { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { v4 as uuid } from "uuid";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import DragIndicatorOutlinedIcon from "@mui/icons-material/DragIndicatorOutlined";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { client } from "../../hooks/Client";
import { useFirestore, useFirestoreOrderBy } from "../../firebase/useFirestore";
import { db, timestamp } from "../../firebase/config.js";
import ScrollToTop from "../../hooks/ScrollToTop";
import useSettings from "../../hooks/Settings";
import Tooltip from "../../components/common/Tooltip";
import FieldCount from "../../components/Questionnaire/FieldCount";
import DeleteModal from "../../components/common/DeleteModal";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import { Auth } from "../../StateManagment/Auth";
import Table from "../../components/common/table/Table.jsx";
import TableCell from "../../components/common/table/TableCell.jsx";
import TableTitleCell from "../../components/common/table/TableTitleCell.jsx";
import ReadOnlyGuard from "../../components/common/ReadOnlyGuard";
import notDeleted from "../../helpers/notDeleted";
import softDeleteDoc from "../../helpers/softDeleteDoc";
import scaleStartLabel from "../../helpers/questionnaire/scaleStartLabel";

const Questionnaires = () => {
  // Context
  const [saved, setSaved] = useContext(SavedIcon);
  const [auth] = useContext(Auth);

  // State
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteDocid, setDeleteDocid] = useState("");
  const [deleteName, setDeleteName] = useState("");
  const [dragArray, setDragArray] = useState([]);

  // Hooks
  ScrollToTop();
  const history = useHistory();
  const { TertairyColor, subscription } = useSettings();

  // Firestore
  const questionnaires = useFirestoreOrderBy("Questionnaires", "Position", "asc");
  const researches = useFirestore("Research");

  // Map each questionnaire ID -> the titles of the research(es) linked to it
  const researchTitlesByQuestionnaire = useMemo(() => {
    const map = {};
    (researches || []).forEach((research) => {
      if (!research.QuestionnaireID) return;
      if (!map[research.QuestionnaireID]) map[research.QuestionnaireID] = [];
      map[research.QuestionnaireID].push(research.Title || "Naamloos onderzoek");
    });
    return map;
  }, [researches]);

  // Keep a local array for instant UI re-order
  useEffect(() => {
    setDragArray(questionnaires || []);
  }, [questionnaires]);

  // Delete modal
  const deleteModalHandler = (e) => {
    setDeleteDocid(e.currentTarget.dataset.docid);
    setDeleteName(e.currentTarget.dataset.deletename);
    setDeleteModal(true);
  };

  // Zacht (ADR-008): de vragenlijst met haar vragen is terug te zetten via de
  // prullenbak. Tot 2026-09-15 was dit een harde delete — de wezen daarvan zijn
  // 2026-10-02 geteld en opgeruimd (inbox #296).
  // AI-CONTEXT: See docs/pitfalls.md#database-hard-to-soft-delete-leaves-old-orphans
  const deleteQuestionnaire = async () => {
    await softDeleteDoc({ collection: "Questionnaires", docid: deleteDocid, authId: auth?.ID, title: deleteName });
    setSaved("flex");
    setDeleteModal(false);
  };

  const addQuestionnaire = () => {
    const newId = uuid();
    db.collection("Questionnaires")
      .doc()
      .set({
        ID: newId,
        Timestamp: timestamp,
        Compagny: client,
        CompagnyID: client,
        EvidenceBased: false,
        Position: (questionnaires?.length || 0) + 1,
        Title: "Vragenlijst " + ((questionnaires?.length || 0) + 1),
      });
  };

  const titleHandler = (e) => {
    const docid = e.target.dataset.docid;
    const title = e.target.value;
    db.collection("Questionnaires")
      .doc(docid)
      .update({ Title: title })
      .then(() => setSaved("flex"));
  };

  // Duplicate questionnaire + nested collections with batches
  const duplicateQuestionaire = async (e) => {
    const sourceTitle = e.currentTarget.dataset.title;
    const sourceQuestionnaireID = e.currentTarget.dataset.id;

    const newQuestionnaireId = uuid();

    // 1) Create the questionnaire
    await db.collection("Questionnaires").doc().set({
      ID: newQuestionnaireId,
      Timestamp: timestamp,
      Compagny: client,
      CompagnyID: client,
      EvidenceBased: false,
      Position: (questionnaires?.length || 0) + 1,
      Title: `${sourceTitle} (kopie)`,
    });

    // 2) Duplicate fields
    const fieldsSnap = await db
      .collection("QuestionnaireFields")
      .where("QuestionnaireID", "==", sourceQuestionnaireID)
      .get();

    // We'll map originalFieldID -> newFieldID so children can reference
    const idMap = new Map();

    // Batch fields in chunks to avoid batch limits
    const chunks = [];
    const fields = [];
    fieldsSnap.forEach((d) => fields.push(d));

    while (fields.length) chunks.push(fields.splice(0, 400)); // safe chunk size

    for (const ch of chunks) {
      const batch = db.batch();
      ch.forEach((docSnap) => {
        const field = docSnap.data();
        const originalFieldID = field.ID;
        const newFieldID = uuid();
        idMap.set(originalFieldID, newFieldID);

        const ref = db.collection("QuestionnaireFields").doc();
        batch.set(ref, {
          Compagny: client,
          CompagnyID: client,
          ID: newFieldID,
          Timestamp: timestamp,
          QuestionnaireID: newQuestionnaireId,
          Type: field.Type,
          Question: field.Question,
          Explainer: field.Explainer,
          ReachStart: field.ReachStart,
          ReachStartLable: scaleStartLabel(field),
          ReachEnd: field.ReachEnd,
          ReachEndLabel: field.ReachEndLabel,
          Key: uuid(),
          Position: field.Position,
          Multiple: field.Multiple,
          SectionID: field.SectionID,
          Color: TertairyColor,
          Title: field.Title ? field.Title : "",
          MatrixBipolar: field.MatrixBipolar === true,
        });
      });
      await batch.commit();
    }

    // 3) Duplicate MultipleQuestionOptions, MatrixQuestionRows, MatrixQuestionColumns for each field
    // For performance: fetch all children for all originalFieldIDs in parallel
    const originalIDs = Array.from(idMap.keys());

    // Helper to chunk IDs for 'in' queries if you ever refactor to use where('Field','in',ids)
    const chunkIds = (arr, size = 10) =>
      Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
        arr.slice(i * size, i * size + size)
      );

    // Options
    for (const chunk of chunkIds(originalIDs, 10)) {
      const optPromises = chunk.map((fid) =>
        db.collection("MultipleQuestionOptions").where("Field", "==", fid).get()
      );
      const optSnaps = await Promise.all(optPromises);
      for (const snap of optSnaps) {
        if (snap.empty) continue;
        const batch = db.batch();
        snap.docs.filter((d) => notDeleted(d.data())).forEach((optDoc) => {
          const option = optDoc.data();
          const newFieldID = idMap.get(option.Field);
          const ref = db.collection("MultipleQuestionOptions").doc();
          batch.set(ref, {
            Option: option.Option,
            Question: option.Question,
            Field: newFieldID,
            Compagny: client,
            CompagnyID: client,
            Timestamp: timestamp,
            Position: option.Position ? option.Position : 0,
            ID: uuid(),
            Color: TertairyColor,
          });
        });
        await batch.commit();
      }
    }

    // Matrix Rows
    for (const chunk of chunkIds(originalIDs, 10)) {
      const rowPromises = chunk.map((fid) =>
        db.collection("MatrixQuestionRows").where("Field", "==", fid).get()
      );
      const rowSnaps = await Promise.all(rowPromises);
      for (const snap of rowSnaps) {
        if (snap.empty) continue;
        const batch = db.batch();
        snap.docs.filter((d) => notDeleted(d.data())).forEach((rowDoc) => {
          const row = rowDoc.data();
          const newFieldID = idMap.get(row.Field);
          const ref = db.collection("MatrixQuestionRows").doc();
          batch.set(ref, {
            Title: row.Title,
            TitleRight: row.TitleRight || "",
            Question: row.Question,
            Field: newFieldID,
            Compagny: client,
            CompagnyID: client,
            Timestamp: timestamp,
            Position: row.Position,
            ID: uuid(),
            Color: TertairyColor,
          });
        });
        await batch.commit();
      }
    }

    // Matrix Columns
    for (const chunk of chunkIds(originalIDs, 10)) {
      const colPromises = chunk.map((fid) =>
        db.collection("MatrixQuestionColumns").where("Field", "==", fid).get()
      );
      const colSnaps = await Promise.all(colPromises);
      for (const snap of colSnaps) {
        if (snap.empty) continue;
        const batch = db.batch();
        snap.docs.filter((d) => notDeleted(d.data())).forEach((colDoc) => {
          const column = colDoc.data();
          const newFieldID = idMap.get(column.Field);
          const ref = db.collection("MatrixQuestionColumns").doc();
          batch.set(ref, {
            Title: column.Title,
            Question: column.Question,
            Field: newFieldID,
            Compagny: client,
            CompagnyID: client,
            Timestamp: timestamp,
            Position: column.Position,
            ID: uuid(),
            Color: TertairyColor,
          });
        });
        await batch.commit();
      }
    }

    setSaved("flex");
  };

  // DRAG & DROP — reorder items and update Firestore
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(dragArray);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setDragArray(items);

    items.forEach((q, idx) => {
      if (!q?.docid) return;
      db.collection("Questionnaires").doc(q.docid).update({ Position: idx + 1 });
    });

    setSaved("flex");
  };

  return (
    <div className="main">
      <div className="main-container">
        <div className="page-header">
          <ListAltOutlinedIcon />
          <h1>Vragenlijsten</h1>
        </div>

        <ReadOnlyGuard edit="measurement.edit">
          {(canEdit) => (
            <Table
              columns={[
                { key: "drag", label: "", searchable: false },
                { key: "title", label: "TITEL" },
                { key: "research", label: "ONDERZOEK", searchable: false },
                { key: "questions", label: "VRAGEN", searchable: false },
                { key: "delete", label: "VERWIJDER", searchable: false },
              ]}
              items={dragArray || []}
              getItemKey={(questionnaire) => questionnaire.ID}
              renderRow={(questionnaire) => (
                <>
                  <TableCell>
                    <DragIndicatorOutlinedIcon />
                  </TableCell>
                  <TableTitleCell
                    value={questionnaire.Title}
                    to={`/${client}/questionairebuilder/${questionnaire.ID}`}
                    onBlur={titleHandler}
                    docid={questionnaire.docid}
                    placeholder="Schrijf hier de titel"
                  />
                  <TableCell>
                    {(() => {
                      const titles =
                        researchTitlesByQuestionnaire[questionnaire.ID] || [];
                      if (titles.length === 0) return "-";
                      return (
                        <>
                          {titles.slice(0, 2).join(", ")}
                          {titles.length > 2 && ` +${titles.length - 2}`}
                        </>
                      );
                    })()}
                  </TableCell>
                  <TableCell
                    onClick={() =>
                      history.push(`/${client}/questionairebuilder/${questionnaire.ID}`)
                    }
                  >
                    <FieldCount id={questionnaire.ID} />
                  </TableCell>
                  <TableCell>
                    {canEdit && (
                      <DeleteOutlinedIcon data-testid='delete-questionnaire'
                        className="table-delete-icon"
                        data-docid={questionnaire.docid}
                        data-deletename={questionnaire.Title}
                        onClick={deleteModalHandler}
                      />
                    )}
                  </TableCell>
                </>
              )}
              searchAccessor={(questionnaire) => questionnaire?.Title || ""}
              searchPlaceholder="Zoeken op titel"
              emptyMessage="Geen vragenlijsten gevonden."
              draggable
              droppableId="questionnaires"
              onDragEnd={handleDragEnd}
              addRow={canEdit ? {
                toolTipContent:
                  subscription === "free"
                    ? "Extra vragenlijsten toevoegen is een premium functie."
                    : "Vragenlijst toevoegen",
                onClick: subscription === "free" ? null : addQuestionnaire,
              } : undefined}
            />
          )}
        </ReadOnlyGuard>

        <DeleteModal
          deleteModal={deleteModal}
          setDeleteModal={setDeleteModal}
          deleteItem={deleteQuestionnaire}
          deleteName={deleteName} softDelete />
      </div>
    </div>
  );
};

export default Questionnaires;
